// src/components/tools/ToolHost.tsx
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTools } from '../../context/ToolContext';
import { useLayerDrawer } from '../../easymap/layerDrawer/LayerDrawerContext';
import { usePermissions } from '../../context/PermissionContext';

// adjust these imports to your actual paths
import { EnhancedDutyLogExplorer } from '../explorers/dutyLogExplorer/DutyLogExplorer';
import WeatherStationExplorer from '../explorers/weatherStationExplorer/WeatherStationExplorer';
import WeatherDataExplorer from '../explorers/weatherDataExplorer/WeatherDataExplorer';
import WildfireExplorer from '../explorers/wildfireExplorer/wildfireExplorer';
import { WildfireDetailModal } from '../explorers/wildfireExplorer/WildfireDetailModal';
import LatLngConverter from '../../easymap/mapcontrols/LatLngConverter/LatLngConverter';
import type { WildfireEntity } from '../../types/wildfire';

import AppModal from '../common/AppModal';
import UserEdit from '../userComponents/UserEdit';
import PasswordReset from '../userComponents/PasswordReset';
import { ThemeSwitcher } from '../ThemeSwitcher';
import { LayerAdminExplorer } from '../explorers/layerAdminExplorer';
import { VARExplorer } from '../explorers/varExplorer';
import VARFlagDialog from '../explorers/varExplorer/VARFlagDialog';
import VARPopupContent from '../explorers/varExplorer/VARPopupContent';
import VAREditModal from '../../easymap/mapcontrols/VARMapTool/VAREditModal';
import { AuditExplorer } from '../explorers/auditExplorer';
import { PermissionManagerExplorer } from '../explorers/permissionManagerExplorer';
import LightningExplorer from '../explorers/lightningExplorer/lightningExplorer';
import AffExplorer from '../explorers/affExplorer/AffExplorer';
import AffAircraftModalHost from '../explorers/affExplorer/AffAircraftModalHost';
import { AFF_FEATURE, AFF_EXPLORE_OP } from '../../shared/affPermissions';
import HotspotExplorer from '../explorers/hotspotExplorer/HotspotExplorer';

import SupportTool from '../../easymap/mapcontrols/SupportTool/SupportTool';
import { PrivateFileTest } from '../test/PrivateFileTest';
import MapPrinting from '../../easymap/mapcontrols/MapPrinting/MapPrinting';
import { FireModelingDashboard } from '../fireModelingDashboard';
import { GISDownloadModal } from '../gisDownload/GISDownloadModal';
import RFBFTool from '../../easymap/mapcontrols/RFBFTool/RFBFTool';
import { ReissueToolsPanel } from '../reissueTools/ReissueToolsPanel';
import ExportNotification from '../common/ExportNotification';

const ToolHost: React.FC = () => {
  const { tools, payloads, openTool, closeTool } = useTools();
  const { refreshLayers, map } = useLayerDrawer();
  const { canDo } = usePermissions();

  // for the case: openTool('wildfireDetail', { fireId: '...' })
  const [wildfireFromId, setWildfireFromId] = useState<WildfireEntity | null>(null);
  const [wildfireLoading, setWildfireLoading] = useState(false);
  const [wildfireError, setWildfireError] = useState<string | null>(null);

  // Bridge: weather-barb / station map popups dispatch `wse:open-detail`
  // expecting WSE to be mounted and listening. If WSE is closed, catch the
  // event here, stash the nesdis on a window-level slot, and open WSE — its
  // own useEffect picks the stashed value up on mount and opens the modal.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ nesdis?: string; name?: string }>).detail;
      if (!detail?.nesdis) return;
      if (!tools.weatherStations) {
        (window as any).__wsePendingDetail = detail;
        openTool('weatherStations');
      }
      // If WSE is already open, its own listener handles the original event.
    };
    window.addEventListener('wse:open-detail', handler);
    return () => window.removeEventListener('wse:open-detail', handler);
  }, [tools.weatherStations, openTool]);

  useEffect(() => {
    if (!tools.wildfireDetail) {
      setWildfireFromId(null);
      setWildfireLoading(false);
      setWildfireError(null);
      return;
    }

    const payload = payloads.wildfireDetail;
    if (!payload) return;

    // if we already have full entity, no fetch needed
    if (payload.wildfire) return;

    // else fetch by id
    if (payload.fireId) {
      const fireId = payload.fireId;
      setWildfireLoading(true);
      setWildfireError(null);

      // TODO: swap in real endpoint
      fetch(`/api/wildfires/${encodeURIComponent(fireId)}`)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((data: WildfireEntity) => {
          setWildfireFromId(data);
          setWildfireLoading(false);
        })
        .catch((err) => {
          console.error('Wildfire detail load failed', err);
          setWildfireError(err.message || 'Could not load wildfire');
          setWildfireLoading(false);
        });
    }
  }, [tools.wildfireDetail, payloads.wildfireDetail]);

  return (
    <>
      {/* Duty logs */}
      {tools.dutylogs && canDo('duty_logs', 'read') &&
        createPortal(
          (() => {
            // whatever EasyMap (or other caller) sent us
            const payload = payloads.dutylogs || {};

            // We agreed the map will dispatch { fireNumber }
            // and sometimes { crosslinkType: 'fire_code' }
            const initialCrosslinkSearch =
              payload.fireNumber
                ? {
                  term: payload.fireNumber,
                  type: payload.crosslinkType || 'fire_code',
                }
                : payload.initialCrosslinkSearch; // fallback if caller already made the object

            console.log('[ToolHost] opening EnhancedDutyLogExplorer with payload →', payload);
            console.log('[ToolHost] derived initialCrosslinkSearch →', initialCrosslinkSearch);

            return (
              <EnhancedDutyLogExplorer
                className="windowed-explorer"
                windowId="new-enhanced-dle"
                initialPosition={{
                  x: window.innerWidth <= 768 ? 10 : 350,
                  y: window.innerWidth <= 768 ? 10 : 200,
                }}
                initialSize={{
                  width:
                    window.innerWidth <= 768
                      ? window.innerWidth - 20
                      : Math.min(1200, window.innerWidth - 370),
                  height:
                    window.innerHeight <= 768
                      ? window.innerHeight - 20
                      : Math.min(700, window.innerHeight - 250),
                }}
                onClose={() => closeTool('dutylogs')}
                onSelectionChange={(selection: any) => {
                  console.log('[ToolHost] Selected duty logs:', selection);
                }}
                onDataChange={() => {
                  console.log('[ToolHost] Duty log data changed');
                }}
                enhancementEnabled={true}
                /* 👇 THIS is the important line */
                initialCrosslinkSearch={initialCrosslinkSearch}
                crosslinkSearchEnabled={true}
                /* when the user clicks a crosslink inside the EDL modal
                   this will get called — you can forward it somewhere else if needed */
                onCrosslinkSearch={(term: string, type: string) => {
                  console.log('[ToolHost] EnhancedDutyLogExplorer wants crosslink:', {
                    term,
                    type,
                  });
                }}
              />
            );
          })(),
          document.body,
        )}

      {/* Weather Station */}
      {tools.weatherStations && canDo('stations', 'read') &&
        createPortal(
          <WeatherStationExplorer
            className="windowed-explorer"
            initialPosition={{
              x: window.innerWidth <= 768 ? 10 : 300,
              y: window.innerWidth <= 768 ? 10 : 150,
            }}
            initialSize={{
              width:
                window.innerWidth <= 768
                  ? window.innerWidth - 20
                  : Math.min(1000, window.innerWidth - 320),
              height:
                window.innerHeight <= 768
                  ? window.innerHeight - 20
                  : Math.min(600, window.innerHeight - 200),
            }}
            onClose={() => closeTool('weatherStations')}
          />,
          document.body,
        )}

      {/* Weather Data */}
      {tools.weatherData && canDo('weather', 'read') &&
        createPortal(
          <WeatherDataExplorer
            className="windowed-explorer"
            initialPosition={{
              x: window.innerWidth <= 768 ? 10 : 350,
              y: window.innerWidth <= 768 ? 10 : 200,
            }}
            initialSize={{
              width:
                window.innerWidth <= 768
                  ? window.innerWidth - 20
                  : Math.min(1200, window.innerWidth - 370),
              height:
                window.innerHeight <= 768
                  ? window.innerHeight - 20
                  : Math.min(700, window.innerHeight - 250),
            }}
            onClose={() => closeTool('weatherData')}
            isWindowed={true}
            windowTitle="Weather Data Explorer"
            resizable={true}
            initialStationId={payloads.weatherData?.stationId}
            initialStartDate={payloads.weatherData?.startDate}
            initialEndDate={payloads.weatherData?.endDate}
          />,
          document.body,
        )}

      {/* Wildfire Explorer (list) */}
      {tools.wildfire && canDo('wildfires', 'read') &&
        createPortal(
          <WildfireExplorer
            className="windowed-explorer"
            initialPosition={{
              x: window.innerWidth <= 768 ? 10 : 400,
              y: window.innerWidth <= 768 ? 10 : 250,
            }}
            initialSize={{
              width:
                window.innerWidth <= 768
                  ? window.innerWidth - 20
                  : Math.min(1200, window.innerWidth - 420),
              height:
                window.innerHeight <= 768
                  ? window.innerHeight - 20
                  : Math.min(700, window.innerHeight - 300),
            }}
            onClose={() => closeTool('wildfire')}
            isWindowed={true}
            windowTitle="Wildfire Explorer"
            resizable={true}
            draggable={true}
            initialFilters={payloads.wildfire?.initialFilters}
            regionFilter={payloads.wildfire?.regionFilter}
          />,
          document.body,
        )}

      {/* Lat/Long */}
      {tools.latlong && canDo('tools', 'lat_long') &&
        createPortal(
          <LatLngConverter isOpen={tools.latlong} onClose={() => closeTool('latlong')} />,
          document.body,
        )}

      {/* Wildfire Detail (entity or id) */}
      {tools.wildfireDetail && canDo('wildfires', 'read') &&
        createPortal(
          (() => {
            const payload = payloads.wildfireDetail;
            const wf = payload?.wildfire || wildfireFromId;

            if (!wf && wildfireLoading) {
              return (
                <AppModal isOpen onClose={() => closeTool('wildfireDetail')} width={400}>
                  <div className="wildfire-detail-modal">
                    <p>Loading wildfire…</p>
                    <button onClick={() => closeTool('wildfireDetail')}>Close</button>
                  </div>
                </AppModal>
              );
            }

            if (!wf && wildfireError) {
              return (
                <AppModal isOpen onClose={() => closeTool('wildfireDetail')} width={400}>
                  <div className="wildfire-detail-modal">
                    <p>{wildfireError}</p>
                    <button onClick={() => closeTool('wildfireDetail')}>Close</button>
                  </div>
                </AppModal>
              );
            }

            if (!wf) return null;

            const initialTab = payloads.wildfireDetail?.tab || 'details';
            return (
              <WildfireDetailModal
                wildfire={wf}
                onClose={() => closeTool('wildfireDetail')}
                initialTab={initialTab}
              />
            );
          })(),
          document.body,
        )}

      {/* Account → User Edit */}
      {tools.userEdit && (
        <AppModal isOpen onClose={() => closeTool('userEdit')} width={440}>
          <UserEdit />
        </AppModal>
      )}

      {/* Account → Password Reset */}
      {tools.passwordReset && (
        <AppModal isOpen onClose={() => closeTool('passwordReset')} width={440}>
          <PasswordReset />
        </AppModal>
      )}

      {/* Account → Theme Preferences */}
      {tools.themePreferences && (
        <AppModal isOpen onClose={() => closeTool('themePreferences')} width={640}>
          <ThemeSwitcher />
        </AppModal>
      )}

      {/* Support → Support Request (Redmine) with bug/feature switch */}
      {tools.support && canDo('support', 'create') && (
        <AppModal isOpen onClose={() => closeTool('support')} width={560} persistent>
          <SupportTool embedded initialType="support" onClose={() => closeTool('support')} />
        </AppModal>
      )}

      {/* Retained in the registry (not the main menu) so they stay independently
          launchable / addable to a toolbox: bug, feature, and open-issues views. */}
      {tools.reportIssue && canDo('support', 'create') && (
        <AppModal isOpen onClose={() => closeTool('reportIssue')} width={520} persistent>
          <SupportTool embedded initialType="bug" onClose={() => closeTool('reportIssue')} />
        </AppModal>
      )}

      {tools.requestFeature && canDo('support', 'create') && (
        <AppModal isOpen onClose={() => closeTool('requestFeature')} width={520} persistent>
          <SupportTool embedded initialType="feature" onClose={() => closeTool('requestFeature')} />
        </AppModal>
      )}

      {tools.viewIssues && canDo('support', 'read') && (
        <AppModal isOpen onClose={() => closeTool('viewIssues')} width={720}>
          <SupportTool embedded initialType="openIssues" onClose={() => closeTool('viewIssues')} />
        </AppModal>
      )}

      {/* Private File Test */}
      {tools.privateFileTest && canDo('audit_log', 'read') && (
        <AppModal isOpen onClose={() => closeTool('privateFileTest')} width={900}>
          <PrivateFileTest />
        </AppModal>
      )}

      {/* Lightning Explorer */}
      {tools.lightning && canDo('lightning', 'read') &&
        createPortal(
          <LightningExplorer
            className="windowed-explorer"
            initialPosition={{
              x: window.innerWidth <= 768 ? 10 : 350,
              y: window.innerWidth <= 768 ? 10 : 150,
            }}
            initialSize={{
              width:
                window.innerWidth <= 768
                  ? window.innerWidth - 20
                  : Math.min(1300, window.innerWidth - 370),
              height:
                window.innerHeight <= 768
                  ? window.innerHeight - 20
                  : Math.min(700, window.innerHeight - 200),
            }}
            onClose={() => closeTool('lightning')}
          />,
          document.body,
        )}

      {/* Aircraft detail modals. Mounted unconditionally alongside the
          explorer: an aircraft can be clicked on the map with the explorer
          closed, and the detail view should still open. */}
      <AffAircraftModalHost />

      {/* AFF Explorer */}
      {tools.aff && canDo(AFF_FEATURE, AFF_EXPLORE_OP) &&
        createPortal(
          <AffExplorer
            className="windowed-explorer"
            initialContext={payloads.aff?.context}
            initialPosition={{
              x: window.innerWidth <= 768 ? 10 : 350,
              y: window.innerWidth <= 768 ? 10 : 150,
            }}
            initialSize={{
              width:
                window.innerWidth <= 768
                  ? window.innerWidth - 20
                  : Math.min(1300, window.innerWidth - 370),
              height:
                window.innerHeight <= 768
                  ? window.innerHeight - 20
                  : Math.min(700, window.innerHeight - 200),
            }}
            onClose={() => closeTool('aff')}
          />,
          document.body,
        )}

      {/* Hotspot Explorer */}
      {tools.hotspotExplorer && canDo('hotspots', 'read') &&
        createPortal(
          <HotspotExplorer
            className="windowed-explorer"
            initialPosition={{
              x: window.innerWidth <= 768 ? 10 : 300,
              y: window.innerWidth <= 768 ? 10 : 130,
            }}
            initialSize={{
              width:
                window.innerWidth <= 768
                  ? window.innerWidth - 20
                  : Math.min(1200, window.innerWidth - 320),
              height:
                window.innerHeight <= 768
                  ? window.innerHeight - 20
                  : Math.min(700, window.innerHeight - 180),
            }}
            onClose={() => closeTool('hotspotExplorer')}
          />,
          document.body,
        )}

      {/* Layer Admin Explorer (admin only) */}
      {tools.layerAdmin && canDo('layer_manager', 'create') &&
        createPortal(
          <LayerAdminExplorer
            className="windowed-explorer"
            initialPosition={{
              x: window.innerWidth <= 768 ? 10 : 400,
              y: window.innerWidth <= 768 ? 10 : 150,
            }}
            initialSize={{
              width:
                window.innerWidth <= 768
                  ? window.innerWidth - 20
                  : Math.min(1400, window.innerWidth - 420),
              height:
                window.innerHeight <= 768
                  ? window.innerHeight - 20
                  : Math.min(800, window.innerHeight - 200),
            }}
            onClose={() => closeTool('layerAdmin')}
            onDataChange={refreshLayers}
            initialLayerId={payloads.layerAdmin?.layerId}
          />,
          document.body,
        )}

      {/* VAR Explorer (Values at Risk) */}
      {tools.varExplorer && canDo('var', 'read') &&
        createPortal(
          <VARExplorer
            className="windowed-explorer"
            initialVarId={payloads.varExplorer?.varId}
            initialPosition={{
              x: window.innerWidth <= 768 ? 10 : 350,
              y: window.innerWidth <= 768 ? 10 : 200,
            }}
            initialSize={{
              width:
                window.innerWidth <= 768
                  ? window.innerWidth - 20
                  : Math.min(1200, window.innerWidth - 370),
              height:
                window.innerHeight <= 768
                  ? window.innerHeight - 20
                  : Math.min(700, window.innerHeight - 250),
            }}
            onClose={() => closeTool('varExplorer')}
          />,
          document.body,
        )}

      {/* VAR Flag Dialog (map popup path) — gate per flag type so the
          two new permissions (var.flag_red, var.flag_sprinkler) work
          independently of the older var.update grant. */}
      {tools.varFlagDialog && payloads.varFlagDialog &&
        ((payloads.varFlagDialog.flagType === 'redFlag' && canDo('var', 'flag_red')) ||
         (payloads.varFlagDialog.flagType === 'sprinkler' && canDo('var', 'flag_sprinkler'))) && (
        <VARFlagDialog
          isOpen={true}
          onClose={() => closeTool('varFlagDialog')}
          entity={payloads.varFlagDialog.entity}
          flagType={payloads.varFlagDialog.flagType}
          onSuccess={() => {
            // Dispatch event so VARLayer can refresh its data
            window.dispatchEvent(new CustomEvent('varFlagToggled'));
          }}
        />
      )}

      {/* VAR Details Modal (map popup → "View Details" button) */}
      {canDo('var', 'read') && <VARPopupContent />}

      {/* VAR Edit Modal (map popup → "Edit" button) */}
      {canDo('var', 'update') && <VAREditModal />}

      {/* Map Printing - triggered by menu or button */}
      {tools.printMap && canDo('tools', 'print_map') && map && (
        <MapPrinting map={map} onClose={() => closeTool('printMap')} />
      )}

      {/* Audit Explorer (admin only) */}
      {tools.auditExplorer && canDo('audit_log', 'read') &&
        createPortal(
          <AuditExplorer
            className="windowed-explorer"
            initialPosition={{
              x: window.innerWidth <= 768 ? 10 : 400,
              y: window.innerWidth <= 768 ? 10 : 150,
            }}
            initialSize={{
              width:
                window.innerWidth <= 768
                  ? window.innerWidth - 20
                  : Math.min(1300, window.innerWidth - 420),
              height:
                window.innerHeight <= 768
                  ? window.innerHeight - 20
                  : Math.min(700, window.innerHeight - 200),
            }}
            onClose={() => closeTool('auditExplorer')}
          />,
          document.body,
        )}

      {/* Permission Manager (admin only) */}
      {tools.permissionManager && canDo('audit_log', 'read') &&
        createPortal(
          <PermissionManagerExplorer
            className="windowed-explorer"
            initialPosition={{
              x: window.innerWidth <= 768 ? 10 : 400,
              y: window.innerWidth <= 768 ? 10 : 150,
            }}
            initialSize={{
              width:
                window.innerWidth <= 768
                  ? window.innerWidth - 20
                  : Math.min(1200, window.innerWidth - 420),
              height:
                window.innerHeight <= 768
                  ? window.innerHeight - 20
                  : Math.min(700, window.innerHeight - 200),
            }}
            onClose={() => closeTool('permissionManager')}
          />,
          document.body,
        )}

      {/* Re-Issue Reports (admin only) */}
      {tools.reIssueReports && canDo('reports', 'reissue') && (
        <ReissueToolsPanel onClose={() => closeTool('reIssueReports')} />
      )}

      {/* GIS Data Download */}
      {tools.gisDownload && canDo('gis', 'read') && (
        <GISDownloadModal
          isOpen={tools.gisDownload}
          onClose={() => closeTool('gisDownload')}
        />
      )}

      {/* Fire Modeling Dashboard (Nomad) - portaled to body for free-floating panel */}
      {tools.fireModeling && canDo('modelling', 'read') &&
        createPortal(
          <div className="fire-modeling-overlay">
            <FireModelingDashboard onClose={() => closeTool('fireModeling')} />
          </div>,
          document.body,
        )}

      {/* RFBF — Regional Fire Behaviour Forecast (Day 1/2/3 animation tool) */}
      {tools.rfbf && canDo('tools', 'rfbf') && map && (
        <RFBFTool map={map} onClose={() => closeTool('rfbf')} />
      )}

      {/* Export Notification — always rendered for authenticated users */}
      <ExportNotification />
    </>
  );
};

export default ToolHost;
